// Session Planner — orchestrates session lifecycle with dynamic question selection
// Multi-cert: sessions are keyed by [certId][sessionId]

import { certPath, push, write, read } from './firebase-store.js';
import { selectQuestion, buildCandidatePool, getCat } from './selector.js';
import { getDueCards } from './scheduler.js';
import {
  WARMUP_COUNT, CONSOLIDATION_COUNT, MAX_SESSION_QUESTIONS,
  DEFAULT_SESSION_MINUTES, NEW_VS_REVIEW_RATIO
} from './constants.js';

// Active sessions in memory: { [certId]: { [sessionId]: session } }
const _sessions = {};

// ── Question Type Diversity ──
const TYPE_DIVERSITY_MIN_TYPES = 3;
const TYPE_DIVERSITY_MAX_RATIO = 0.6;

/**
 * Start a new study session (cert-scoped).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {object} sessionConfig - { mode, maxDuration, domainFocus, newVsReviewRatio }
 * @returns {object} { sessionToken, constraints, shape }
 */
export async function startSession(userId, certId, sessionConfig = {}) {
  const sessionId = Date.now() + '-' + Math.random().toString(36).substr(2, 6);

  const config = {
    mode: sessionConfig.mode || 'mixed',
    maxDuration: sessionConfig.maxDuration || DEFAULT_SESSION_MINUTES,
    domainFocus: sessionConfig.domainFocus || null,
    newVsReviewRatio: sessionConfig.newVsReviewRatio ?? NEW_VS_REVIEW_RATIO,
    maxQuestions: sessionConfig.maxQuestions || MAX_SESSION_QUESTIONS
  };

  const session = {
    sessionId,
    userId,
    certId,
    config,
    startTime: new Date().toISOString(),
    position: 0,
    answers: [],
    seenQuestions: new Set(),
    correctCount: 0,
    domainCounts: {},
    typeCounts: {},
    phase: 'warmup',
    active: true
  };

  if (!_sessions[certId]) _sessions[certId] = {};
  _sessions[certId][sessionId] = session;

  return {
    sessionToken: sessionId,
    constraints: config,
    shape: {
      warmup: WARMUP_COUNT,
      consolidation: CONSOLIDATION_COUNT,
      maxQuestions: config.maxQuestions
    }
  };
}

/**
 * Get the next question for an active session (cert-scoped).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {string} sessionToken
 * @param {Array} questionBank - all questions with metadata
 * @returns {object|null} { questionId, reasonCode, source, phase, stopSignal? }
 */
export async function getNextQuestion(userId, certId, sessionToken, questionBank) {
  const certSessions = _sessions[certId] || {};
  const session = certSessions[sessionToken];
  if (!session || !session.active) {
    return { questionId: null, stopSignal: { type: 'sessionComplete', recommendedAction: 'end' } };
  }

  // Check if session should end
  if (session.position >= session.config.maxQuestions) {
    return {
      questionId: null,
      phase: 'complete',
      stopSignal: { type: 'sessionComplete', recommendedAction: 'end' }
    };
  }

  // Determine phase
  const pos = session.position;
  const maxQ = session.config.maxQuestions;
  let phase;
  if (pos < WARMUP_COUNT) {
    phase = 'warmup';
  } else if (pos >= maxQ - CONSOLIDATION_COUNT) {
    phase = 'consolidation';
  } else {
    phase = 'core';
  }
  session.phase = phase;

  // Get due cards from FSRS (cert-scoped)
  const dueCards = await getDueCards(userId, certId);

  // Filter placeholder questions before building candidate pool
  const validQuestions = questionBank.filter(q => q.ans !== '?');

  // Build candidate pool
  const candidates = buildCandidatePool({
    questionBank: validQuestions,
    dueCards,
    sessionConstraints: {
      phase,
      domainFocus: session.config.domainFocus,
      newVsReviewRatio: session.config.newVsReviewRatio
    },
    seenThisSession: session.seenQuestions
  });

  if (candidates.length === 0) {
    return {
      questionId: null,
      phase,
      stopSignal: { type: 'poolExhausted', recommendedAction: 'end' }
    };
  }

  // Apply type diversity filtering during core phase
  const diversifiedCandidates = (phase === 'core')
    ? _applyTypeDiversity(candidates, session)
    : candidates;

  // Determine which domain CAT to use (cert-scoped)
  const domainFocus = session.config.domainFocus;
  const cat = getCat(certId, domainFocus || 1);

  // Select via jsCAT MFI
  const questionId = selectQuestion(cat, diversifiedCandidates);

  // Build reason code
  let reasonCode = 'ability-match';
  const selectedQ = candidates.find(q => q.questionId === questionId || q.id === questionId);
  if (selectedQ) {
    const isDue = dueCards.some(c => c.questionId === questionId);
    if (isDue) {
      reasonCode = 'fsrs-due: review scheduled';
    } else {
      reasonCode = 'ability-match: theta=' + cat.theta.toFixed(2) + ', difficulty=' + (selectedQ.irtDifficulty || 0).toFixed(2);
    }
  }

  return {
    questionId,
    reasonCode,
    source: 'calibrated',
    phase,
    stopSignal: null
  };
}

/**
 * Record that a question was answered in the session (cert-scoped).
 * @param {string} certId
 * @param {string} sessionToken
 * @param {string} questionId
 * @param {boolean} correct
 * @param {number} domain
 * @param {number} responseMs
 * @param {string} questionType
 */
export function recordSessionAnswer(certId, sessionToken, questionId, correct, domain, responseMs, questionType) {
  const certSessions = _sessions[certId] || {};
  const session = certSessions[sessionToken];
  if (!session) return;

  session.position++;
  session.seenQuestions.add(questionId);
  session.answers.push({ questionId, correct, position: session.position, responseMs: responseMs || 0, questionType: questionType || 'mcq' });
  if (correct) session.correctCount++;
  session.domainCounts[domain] = (session.domainCounts[domain] || 0) + 1;

  // Track question type for diversity
  const qType = questionType || 'mcq';
  session.typeCounts[qType] = (session.typeCounts[qType] || 0) + 1;
}

/**
 * End a session and persist the record to Firebase (cert-scoped).
 *
 * @param {string} userId
 * @param {string} certId
 * @param {string} sessionToken
 * @returns {object} session summary
 */
export async function endSession(userId, certId, sessionToken) {
  const certSessions = _sessions[certId] || {};
  const session = certSessions[sessionToken];
  if (!session) return null;

  session.active = false;
  const endTime = new Date().toISOString();

  const summary = {
    startTime: session.startTime,
    endTime,
    mode: session.config.mode,
    accuracy: session.position > 0 ? session.correctCount / session.position : 0,
    domainBreakdown: session.domainCounts,
    fatigueDetected: false,
    endReason: session.position >= session.config.maxQuestions ? 'complete' : 'manual',
    questionCount: session.position
  };

  // Persist to Firebase (cert-scoped)
  await write(certPath(userId, certId, 'sessions/' + session.sessionId), summary);

  // Cleanup memory
  delete certSessions[sessionToken];

  return summary;
}

/**
 * Apply question type diversity filtering to the candidate pool.
 */
function _applyTypeDiversity(candidates, session) {
  if (candidates.length <= 1 || session.position < WARMUP_COUNT) {
    return candidates;
  }

  const typeCounts = session.typeCounts || {};
  const totalAnswered = session.position;
  const typesUsed = Object.keys(typeCounts);

  const availableTypes = {};
  for (const q of candidates) {
    const t = q.questionType || q.type || 'mcq';
    if (!availableTypes[t]) availableTypes[t] = [];
    availableTypes[t].push(q);
  }
  const availableTypeKeys = Object.keys(availableTypes);

  if (availableTypeKeys.length <= 1) return candidates;

  const unseenTypes = availableTypeKeys.filter(t => !typeCounts[t]);
  const overRepresented = typesUsed.filter(t =>
    typeCounts[t] / totalAnswered > TYPE_DIVERSITY_MAX_RATIO && totalAnswered >= 3
  );

  if (typesUsed.length < TYPE_DIVERSITY_MIN_TYPES && unseenTypes.length > 0) {
    const boosted = [];
    const rest = [];
    for (const q of candidates) {
      const t = q.questionType || q.type || 'mcq';
      if (unseenTypes.indexOf(t) !== -1) {
        boosted.push(q);
      } else {
        rest.push(q);
      }
    }
    return boosted.concat(rest);
  }

  if (overRepresented.length > 0) {
    const deprioritized = [];
    const preferred = [];
    for (const q of candidates) {
      const t = q.questionType || q.type || 'mcq';
      if (overRepresented.indexOf(t) !== -1) {
        deprioritized.push(q);
      } else {
        preferred.push(q);
      }
    }
    if (preferred.length >= 5) {
      return preferred.concat(deprioritized);
    }
  }

  return candidates;
}

/**
 * Get current session state (for internal use).
 * Searches across all certs to find the session.
 */
export function getSessionState(sessionToken) {
  for (const certId in _sessions) {
    if (_sessions[certId][sessionToken]) {
      return _sessions[certId][sessionToken];
    }
  }
  return null;
}
